import { Drawer, Pagination, Table } from '@/components/commons';
import { DeleteContent } from '@/components/commons/DeleteContent';
import { ENDPOINT } from '@/services/endpoint';
import { IResponse, IResponseError } from '@/types/common';
import {
  convertRequest,
  handleErrorCommon,
  notificationMessage,
  notificationModal,
  NOTIFY_STATUS,
  articleStatus,
  Status,
  statusJP,
} from '@/utils';
import {
  ERROR,
  ERROR_MESSAGE_FIELD,
  SUCCESS,
  SUCCESS_MESSAGE,
} from '@/utils/constants/messages';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button, Image, Input, TableColumnsType, Tag } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { FormProvider, useForm } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { FORM_NAME } from '../Drawers/OfferForm/constants';
import { formSchemaFn } from '../Drawers/OfferForm/formSchemaFn';
import {
  ICreateOfferRequest,
  IUpdateOfferRequest,
} from '@/types/request/offer';
import { IUploadImageRequest } from '@/types/request/upload';
import { IGetDetailOfferResponse } from '@/types/response/offer';
import { ImageService } from '@/services/image';
import { OfferService } from '@/services/offer';
import { OfferForm } from '../Drawers';
import { ITag } from '@/types/response/tag';

interface IProps {
  title?: string;
}

interface IParams {
  title?: string;
  limit: number;
  page: number;
}

export interface ContentImage {
  url?: string;
  file?: File;
  previewUrl?: string;
}

const defaultCover: ContentImage | null = null;

export const OfferList: React.FC<IProps> = ({ title }) => {
  const queryClient = useQueryClient();
  const [currentId, setCurrentId] = useState<number | null>(null);
  const [openDrawer, setOpenDrawer] = useState<boolean>(false);
  const [detail, setDetail] = useState<IGetDetailOfferResponse>();
  const [isNotFoundDetail, setIsNotFoundDetail] = useState<boolean>(false);
  const [params, setParams] = useState<IParams>({
    limit: 10,
    page: 1,
  });

  const { data, refetch, isFetching } = useQuery(
    [ENDPOINT.OFFER.GET_LIST, params],
    () => OfferService.getList(convertRequest(params)),
  );

  const methods = useForm<any>({
    mode: 'onChange',
    resolver: yupResolver(formSchemaFn(!!currentId)),
    reValidateMode: 'onChange',
  });

  const { handleSubmit, reset, setError, watch, setValue } = methods;

  const handleCancel = () => {
    setOpenDrawer(false);
    setCurrentId(null);
    setDetail(undefined);
    reset({ coverImage: defaultCover });
  };

  const handleCreate = async (
    values: ICreateOfferRequest & {
      coverImage?: ContentImage | null;
    },
  ) => {
    const { coverImage, tagNames, useCoupon, ...rest } = values as any;
    const payload: ICreateOfferRequest = {
      ...rest,
      tagNames: (tagNames || [])
        .map((t: any) => String(t).trim())
        .filter((t: string) => t.length),
      useCoupon: Boolean(!!useCoupon),
      status: values?.status,
    } as any;
    try {
      if (coverImage?.file) {
        const response = await ImageService.uploadFile(coverImage.file);
        payload.coverImageUrl = response?.data?.url;
      }
      await createOffer(payload);
    } catch (error) {
      console.log({ error });
    }
  };

  const { mutate: createOffer, isLoading: isLoadingCreate } = useMutation(
    (body: ICreateOfferRequest) => OfferService.create(body),
    {
      onSuccess: () => {
        notificationMessage({
          message:
            SUCCESS_MESSAGE[
            SUCCESS.CREATE_OFFER as keyof typeof SUCCESS_MESSAGE
            ],
          type: NOTIFY_STATUS.SUCCESS,
        });
        queryClient.invalidateQueries([ENDPOINT.TAG.GET_LIST]);
        refetch();
        handleCancel();
        setParams({ ...params, page: 1 });
      },
      onError: (response: IResponseError) => {
        handleErrorCommon(response, setError);
      },
    },
  );

  const { mutateAsync: uploadFile, isLoading: isLoadingUpload } = useMutation(
    (file: File) => ImageService.uploadFile(file),
    {
      onError: (response: IResponseError) => {
        handleErrorCommon(response, setError);
      },
    },
  );

  const { isFetching: isFetchingDetail } = useQuery(
    [ENDPOINT.OFFER.GET_DETAIL.replace(':id', String(currentId))],
    () => OfferService.getDetail(currentId!),
    {
      enabled: !!currentId,
      onSuccess: (response: IResponse<IGetDetailOfferResponse>) => {
        const data = response?.data;
        setDetail(data);
        const payload: ICreateOfferRequest = {
          title: data?.title,
          description: data?.description,
          partnerId: (data?.partner as any)?.id || undefined,
          tagNames: data?.tags?.map((t: ITag) => t.name) || [],
          useCoupon: Number(data?.useCoupon) ? 1 : 0,
          couponCode: data?.couponCode || undefined,
          couponExpiredAt: data?.couponExpiredAt
            ? moment(data?.couponExpiredAt)
            : undefined,
          details: data?.details || undefined,
          ctaText: data?.ctaText || undefined,
          linkUrl: data?.linkUrl || undefined,
          publicAt: moment(data?.publicAt),
          publicEndAt: data?.publicEndAt
            ? moment(data?.publicEndAt)
            : undefined,
          status: data?.status,
          isDisabled:
            data?.publicEndAt && moment().isAfter(moment(data?.publicEndAt)),
        } as any;
        reset({
          formName: FORM_NAME.UPDATE_OFFER,
          ...payload,
          coverImage: data?.imageCover?.url
            ? { url: data?.imageCover?.url, previewUrl: data?.imageCover?.url }
            : null,
        });
      },
      onError: (error: IResponseError) => {
        handleErrorCommon(error, setError);
        setIsNotFoundDetail(true);
      },
    },
  );

  const handleUpdate = async (
    values: IUpdateOfferRequest & {
      coverImage?: ContentImage | null;
    },
  ) => {
    const { coverImage, tagNames, useCoupon, ...rest } = values as any;
    const payload: IUpdateOfferRequest = {
      ...rest,
      tagNames: (tagNames || [])
        .map((t: any) => String(t).trim())
        .filter((t: string) => t.length),
      useCoupon: Boolean(!!useCoupon),
      status: values?.status,
    } as any;
    try {
      if (coverImage?.file) {
        const response = await ImageService.uploadFile(coverImage.file);
        payload.coverImageUrl = response?.data?.url;
      } else if (coverImage?.url) {
        payload.coverImageUrl = coverImage.url;
      }
      await updateOffer({ body: payload, id: Number(currentId) });
    } catch (error) {
      console.log({ error });
    }
  };

  const { mutate: updateOffer, isLoading: isLoadingUpdate } = useMutation(
    (payload: { body: IUpdateOfferRequest; id: number }) =>
      OfferService.update(payload.body, payload.id),
    {
      onSuccess: () => {
        notificationMessage({
          message:
            SUCCESS_MESSAGE[
            SUCCESS.UPDATE_OFFER as keyof typeof SUCCESS_MESSAGE
            ],
          type: NOTIFY_STATUS.SUCCESS,
        });
        queryClient.invalidateQueries([ENDPOINT.TAG.GET_LIST]);
        refetch();
        handleCancel();
        setParams({ ...params, page: 1 });
      },
      onError: (error: IResponseError) => {
        handleErrorCommon(error, setError);
        if (error.error === ERROR.OFFER_NOT_FOUND) {
          setOpenDrawer(false);
          refetch();
        }
      },
    },
  );

  const { mutate: deleteOffer, isLoading: isLoadingDelete } = useMutation(
    (id: number) => OfferService.delete(id),
    {
      onSuccess: () => {
        notificationMessage({
          message:
            SUCCESS_MESSAGE[
            SUCCESS.DELETE_OFFER as keyof typeof SUCCESS_MESSAGE
            ],
          type: NOTIFY_STATUS.SUCCESS,
        });
        refetch();
        handleCancel();
        setParams({ ...params, page: 1 });
      },
      onError: (error: IResponseError & { code?: string }) => {
        if (
          error.error === ERROR.DELETE_UNSUCCESS ||
          error.code === ERROR.DELETE_UNSUCCESS
        ) {
          notificationMessage({
            message: ERROR_MESSAGE_FIELD.DELETE_UNSUCCESS(
              detail?.title || 'オファー',
            ),
            type: NOTIFY_STATUS.ERROR,
          });
          return;
        }
        handleErrorCommon(error, setError);
        if (error.error === ERROR.OFFER_RELOAD_DATA) {
          setOpenDrawer(false);
          refetch();
        }
      },
    },
  );

  const columns: TableColumnsType = [
    {
      title: 'ID',
      dataIndex: 'id',
      align: 'center',
      width: '5%',
    },
    { title: 'タイトル', dataIndex: 'title', width: '25%' },
    {
      title: '提供企業',
      render: (value, record: any) => record?.partner?.companyName || '-',
      width: '15%',
    },
    {
      title: 'クーポン利用',
      width: '5%',
      render: (value, record: any) => (
        <Tag color={record?.useCoupon ? 'green' : 'default'}>
          {record?.useCoupon ? 'あり' : 'なし'}
        </Tag>
      ),
    },
    {
      title: '公開日時',
      width: '15%',
      render: (value, record: any) =>
        `${moment(record.publicAt).format('YYYY年MM月DD日 HH:mm')}`,
    },
    {
      title: '公開終了日時',
      width: '15%',
      dataIndex: 'publicEndAt',
      render: (value, record) =>
        record?.publicEndAt
          ? `${moment(record.publicEndAt).format('YYYY年MM月DD日 HH:mm')}`
          : '-',
    },
    {
      title: '公開ステータス',
      width: '10%',
      dataIndex: 'status',
    },
  ];

  const onEdit = (record: any) => {
    setCurrentId(record.id);
    setOpenDrawer(true);
  };

  useEffect(() => {
    if (openDrawer) {
      setTimeout(() => {
        const bodies = document.querySelectorAll(
          '.c-drawer .ant-drawer-body',
        ) as NodeListOf<HTMLElement>;
        bodies.forEach((el) => {
          el.scrollTop = 0;
        });
      }, 0);
    }
  }, [openDrawer, currentId, detail]);

  const onChangePagination = (page: number, pageSize: number) => {
    setParams({ ...params, page, limit: pageSize });
  };

  const onSubmit = (values: any) => {
    if (!!currentId) return handleUpdate(values);
    return handleCreate(values);
  };

  const handleDelete = (record: any) => {
    notificationModal({
      type: NOTIFY_STATUS.CONFIRM,
      title: '削除しますか？',
      content: <DeleteContent name='タイトル' content={record?.title} />,
      maskClosable: false,
      onOk: () => deleteOffer(record?.id),
      okText: '削除する',
      cancelText: 'キャンセル',
    });
  };

  return (
    <>
      <Helmet>
        <title>{title}</title>
      </Helmet>
      <div className='flex flex-col gap-[16px] mx-auto w-full max-w-[1280px]'>
        <div className='flex justify-between'>
          <div className='flex justify-start gap-[6px] items-center'>
            <Input
              className='w-[300px]'
              prefix={<SearchOutlined className='text-[20px]' />}
              placeholder='タイトル'
              onChange={(e) => {
                setParams({ ...params, page: 1, title: e.target.value });
              }}
            />
          </div>
          <header className='text-right'>
            <Button
              type='primary'
              onClick={() => {
                setOpenDrawer(true),
                  reset({ formName: FORM_NAME.CREATE_OFFER }),
                  setValue('status', Status.PUBLIC);
                setValue('useCoupon', 0);
              }}
            >
              新規作成
            </Button>
          </header>
        </div>

        <div className='flex justify-between flex-col h-full'>
          <Table
            rowKey='id'
            scroll={{ x: 800 }}
            bordered
            loading={isFetching}
            dataSource={data?.data?.data || []}
            columns={columns}
            pagination={false}
            onRow={(record) => ({
              onClick: () => {
                setOpenDrawer(true);
                setCurrentId(record.id);
                setIsNotFoundDetail(false);
              },
              style: {
                cursor: 'pointer',
              },
            })}
            locale={{
              emptyText: 'オファーはまだありません',
            }}
          />
          {data?.data?.pagination?.total! > 10 && (
            <Pagination
              className='pagination-table'
              total={data?.data.pagination.total || 0}
              pageSize={params.limit}
              current={params.page}
              showSizeChanger={false}
              onChange={onChangePagination}
            />
          )}
        </div>
      </div>

      <FormProvider {...methods}>
        <Drawer
          open={openDrawer}
          title={currentId ? 'オファー詳細' : 'オファー新規作成'}
          onCancel={handleCancel}
          loading={
            isLoadingCreate || isLoadingUpdate || isFetchingDetail || isFetching
          }
          isDeleted={!!currentId}
          okText={!currentId ? '保存 ' : '更新 '}
          maskClosable={false}
          onOk={handleSubmit(onSubmit)}
          onDelete={() => handleDelete(detail!)}
        >
          <OfferForm
            isLoading={
              isLoadingCreate ||
              isLoadingUpdate ||
              isFetchingDetail ||
              isFetching
            }
            offer={detail}
          />
        </Drawer>
      </FormProvider>
    </>
  );
};
